<script>
import { mapGetters } from 'vuex';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Thumbnail from '../Thumbnail.vue';
import MessagePreview from './MessagePreview.vue';
import router from '../../../routes';
import { frontendURL, conversationUrl } from '../../../helper/URLHelper';
import InboxName from '../InboxName.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

export default {
  components: {
    CardLabels,
    InboxName,
    Thumbnail,
    ConversationContextMenu,
    TimeAgo,
    MessagePreview,
    PriorityMark,
    SLACardLabel,
    ContextMenu,
  },
  mixins: [inboxMixin],
  props: {
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    hideInboxName: {
      type: Boolean,
      default: false,
    },
    hideThumbnail: {
      type: Boolean,
      default: false,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enableContextMenu: {
      type: Boolean,
      default: false,
    },
    aiAgentName: {
      type: String,
      default: null,
    },
    hasIaAgent: {
      type: Boolean,
      default: false,
    },
    isInSelectionMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'contextMenuToggle',
    'assignAgent',
    'assignLabel',
    'assignTeam',
    'markAsUnread',
    'assignPriority',
    'updateConversationStatus',
    'selectConversation',
    'deSelectConversation',
  ],
  data() {
    return {
      hovered: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
      isProcessingClick: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxesList: 'inboxes/getInboxes',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      selectedConversationIds: 'bulkActions/getSelectedConversationIds',
    }),
    bulkActionCheck() {
      return !this.hideThumbnail;
    },
    chatMetadata() {
      return this.chat.meta || {};
    },

    assignee() {
      return this.chatMetadata.assignee || {};
    },

    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },

    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },

    unreadCount() {
      return this.chat.unread_count;
    },

    hasUnread() {
      return this.unreadCount > 0;
    },

    isInboxNameVisible() {
      return !this.activeInbox;
    },

    lastMessageInChat() {
      const lastMessage = getLastMessage(this.chat);
      return lastMessage;
    },

    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },

    showInboxName() {
      return (
        !this.hideInboxName &&
        this.isInboxNameVisible &&
        this.inboxesList.length > 1
      );
    },
    inboxName() {
      const stateInbox = this.inbox;
      return stateInbox.name || '';
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    hasAtendimentoHumanoLabel() {
      return this.chat?.labels?.includes('atendimento-humano');
    },
    hasAdsCampaign() {
      const campaignValue = this.currentContact?.custom_attributes?.source_app_campaign;
      return campaignValue && campaignValue.toString().trim() !== '';
    },
    adsCampaignValue() {
      return this.currentContact?.custom_attributes?.source_app_campaign || '';
    },
    shouldShowCheckbox() {
      // isInSelectionMode is now a boolean value directly
      const inSelectionMode = this.isInSelectionMode || false;
      // Also show if any conversation is selected (for backwards compatibility)
      const anySelected = this.selectedConversationIds && this.selectedConversationIds.length > 0;
      const result = inSelectionMode || anySelected || this.hovered || this.selected;

      return result;
    },
    isGroupConversation() {
      return this.currentContact?.identifier?.endsWith('@g.us') || false;
    },
    displayContactName() {
      return this.currentContact.name?.replace(/\(GROUP\)/gi, '').trim() || this.currentContact.name;
    },
    displayContactNameHtml() {
      return formatFlagDisplayText(this.displayContactName || '');
    },
    conversationClasses() {
      // Priority 1: Selected state (highest priority)
      if (this.selected) {
        return 'conversation-selected bg-woot-25 dark:bg-woot-900 hover:bg-woot-50 dark:hover:bg-woot-800/50';
      }

      // Priority 2: Active/Viewing state (no hover)
      if (this.isActiveChat) {
        return 'active bg-n-slate-3';
      }

      // Priority 3: Human attendance state
      if (this.hasAtendimentoHumanoLabel) {
        return 'bg-yellow-100/50 dark:bg-yellow-600/20 hover:bg-yellow-200/40 dark:hover:bg-yellow-600/30';
      }

      // Priority 4: Default state (with hover)
      return 'hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3';
    },
  },
  methods: {
    onCardClick(e) {
      // isInSelectionMode is now a boolean directly
      const inSelectionMode = this.isInSelectionMode || false;

      // If in selection mode OR any conversation is selected, handle as selection toggle
      if (inSelectionMode || (this.selectedConversationIds && this.selectedConversationIds.length > 0)) {
        // Prevent text selection when using SHIFT
        if (e.shiftKey) {
          e.preventDefault();
          // Clear any existing text selection
          window.getSelection().removeAllRanges();
        }
        this.onSelectConversation(!this.selected, e.shiftKey);
        return;
      }

      const { activeInbox, chat } = this;
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox,
          id: chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        })
      );

      if (e.metaKey || e.ctrlKey) {
        window.open(
          window.chatwootConfig.hostURL + path,
          '_blank',
          'noopener noreferrer nofollow'
        );
        return;
      }
      if (this.isActiveChat) {
        return;
      }

      router.push({ path });
    },
    onCardHover() {
      this.hovered = !this.hideThumbnail;
    },
    onCardLeave() {
      this.hovered = false;
    },
    onSelectConversation(checked, shiftKey = false) {
      const action = checked ? 'selectConversation' : 'deSelectConversation';
      this.$emit(action, this.chat.id, this.inbox.id, shiftKey);
    },
    onCheckboxClick(e) {
      // Prevent double clicks
      if (this.isProcessingClick) {
        return;
      }

      // Prevent text selection when using SHIFT
      if (e.shiftKey) {
        e.preventDefault();
        // Clear any existing text selection
        window.getSelection().removeAllRanges();
      }

      this.isProcessingClick = true;
      this.onSelectConversation(!this.selected, e.shiftKey);

      // Reset flag after a short delay
      setTimeout(() => {
        this.isProcessingClick = false;
      }, 100);
    },
    openContextMenu(e) {
      if (!this.enableContextMenu) return;
      e.preventDefault();
      this.$emit('contextMenuToggle', true);
      this.contextMenu.x = e.pageX || e.clientX;
      this.contextMenu.y = e.pageY || e.clientY;
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('contextMenuToggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onUpdateConversation(status, snoozedUntil) {
      this.closeContextMenu();
      this.$emit(
        'updateConversationStatus',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    async onAssignAgent(agent) {
      this.$emit('assignAgent', agent, [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignLabel(label) {
      this.$emit('assignLabel', [label.title], [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignTeam(team) {
      this.$emit('assignTeam', team, this.chat.id);
      this.closeContextMenu();
    },
    async markAsUnread() {
      this.$emit('markAsUnread', this.chat.id);
      this.closeContextMenu();
    },
    async assignPriority(priority) {
      this.$emit('assignPriority', priority, this.chat.id);
      this.closeContextMenu();
    },
  },
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full px-3 py-0 cursor-pointer conversation rounded-xl ml-2 mr-1 group"
    :class="[
      conversationClasses,
      {
        'unread-chat': hasUnread,
        'has-inbox-name': showInboxName,
        'selection-mode': isInSelectionMode || (selectedConversationIds && selectedConversationIds.length > 0),
      }
    ]"
    @mouseenter="onCardHover"
    @mouseleave="onCardLeave"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div v-if="bulkActionCheck" class="relative">
      <Thumbnail
        :src="currentContact.thumbnail"
        :badge="inboxBadge"
        :username="currentContact.name"
        :status="currentContact.availability_status"
        size="40px"
      />
      <label
        v-if="shouldShowCheckbox"
        class="absolute bottom-0 right-0 w-5 h-5 rounded-full flex items-center justify-center cursor-pointer shadow-md border-2 border-white dark:border-slate-800"
        :class="selected ? 'bg-woot-500' : 'bg-slate-400 dark:bg-slate-600'"
        @click.stop.prevent="onCheckboxClick"
      >
        <input
          :value="selected"
          :checked="selected"
          class="hidden"
          type="checkbox"
        />
        <fluent-icon
          icon="checkmark"
          size="12"
          :class="selected ? 'text-white brightness-125' : 'text-slate-200 dark:text-slate-400'"
        />
      </label>
    </div>
    <div
      class="px-0 py-3 flex-1 w-[calc(100%-40px)]"
    >
      <div class="flex conversation-card--meta">
        <div class="flex items-center gap-2">
          <InboxName v-if="showInboxName" :inbox="inbox" />
          <span
            v-if="hasAdsCampaign"
            class="inline-flex items-center py-1 px-2 leading-3 whitespace-nowrap bg-woot-500 text-white text-xs rounded-full font-medium"
            :class="{ 'ml-2': !showInboxName }"
            v-tooltip.top-end="'Lead do ' + adsCampaignValue"
          >
            ADS
          </span>
          <span
            v-if="hasIaAgent && aiAgentName && !hasAtendimentoHumanoLabel && !isGroupConversation"
            class="inline-flex items-center py-1 px-2 leading-3 whitespace-nowrap bg-purple-600 text-white text-xs rounded-full"
            :class="{ 'ml-2': !showInboxName }"
            v-tooltip.top-end="aiAgentName + ' (IA)'"
          >
            IA
          </span>
          <span
            v-if="hasAtendimentoHumanoLabel"
            class="bg-yellow-500/60 text-yellow-700 dark:bg-yellow-700 dark:text-yellow-500/80 text-xs px-2 py-0.5 rounded-full font-medium overflow-hidden whitespace-nowrap"
            :class="{ 'ml-2': !showInboxName }"
          >
            <span class="text-ellipsis overflow-hidden whitespace-nowrap max-w-[90px]">Em atendimento</span>
          </span>
        </div>
        <div class="flex gap-2 ml-2 rtl:mr-2 rtl:ml-0">
          <span
            v-if="showAssignee && assignee.name"
            class="inbox--name flex items-center py-1 px-2 leading-3 bg-n-slate-3 text-n-slate-11 text-xs my-0 rounded-full max-w-[140px]"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11 flex-shrink-0" />
            <span class="text-ellipsis overflow-hidden whitespace-nowrap min-w-0">{{ assignee.name }}</span>
          </span>
          <PriorityMark :priority="chat.priority" />
        </div>
      </div>
      <div class="flex justify-between items-center ml-2 pt-0.5">
        <h4
          class="conversation--user text-sm my-0 capitalize text-ellipsis overflow-hidden whitespace-nowrap flex-1 mr-2 text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
          v-dompurify-html="displayContactNameHtml"
        ></h4>
        <span class="font-normal leading-4 text-sm text-n-slate-10">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
          />
        </span>
      </div>
      <div class="flex justify-between items-center ml-2">
        <MessagePreview
          v-if="lastMessageInChat"
          :message="lastMessageInChat"
          class="conversation--message my-0 leading-6 h-6 flex-1 mr-2 text-sm"
          :class="hasUnread ? 'font-medium text-n-slate-12' : 'text-n-slate-11'"
        />
        <p
          v-else
          class="conversation--message text-n-slate-11 text-sm my-0 leading-6 h-6 flex-1 mr-2 overflow-hidden text-ellipsis whitespace-nowrap"
          :class="hasUnread ? 'font-medium text-n-slate-12' : 'text-n-slate-11'"
        >
          <fluent-icon
            size="16"
            class="-mt-0.5 align-middle inline-block text-n-slate-10 mr-1"
            icon="info"
          />
          <span>
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
        <span
          v-if="hasUnread"
          class="unread shadow-lg rounded-full text-xxs font-semibold h-5 leading-5 min-w-[1.25rem] px-1.5 py-0 text-center text-white bg-n-teal-9 flex-shrink-0"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <CardLabels :conversation-labels="chat.labels.filter(label => label !== 'atendimento-humano')" class="mt-0.5 mx-2 mb-0">
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
      />
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.conversation {

  &.compact {
    @apply pl-0;

    .conversation-card--meta {
      @apply ltr:pr-4 rtl:pl-4;
    }

    .conversation--details {
      @apply rounded-sm ml-0 pl-5 pr-2;
    }
  }

  &::v-deep .user-thumbnail-box {
    @apply mt-4;
  }

  &.has-inbox-name {
    &::v-deep .user-thumbnail-box {
      @apply mt-8;
    }

    .conversation--meta {
      @apply mt-4;
    }
  }

  /* Prevent text selection when in selection mode */
  &.selection-mode {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

}
</style>
